import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit, Output,
  PLATFORM_ID,
  SimpleChanges
} from '@angular/core';
import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { RequestOrder } from '../../flux-options/flux-options-order/flux-options-order.component';
import { ActivatedRoute } from '@angular/router';
import { modalEntranceAnimation } from '../../../animations/entrances.animation';
import { ToastrService } from '../../../services/utilities/toastr.service';
import { MISC_CONST } from '../../../../utils/const/misc';

/* Services & models */
import { SeoService } from '../../../services/utilities/seo.service';
import {
  ErrorHandlerStrategy,
  Media,
  User,
  WebServiceOptions,
  HydraCollection
} from '@adeo/ngx-kozikaza-api';
import { DataLayerService } from '../../../services/utilities/data-layer.service';
/* Animations */
import { fadeInAnimation } from '../../../animations/fade-in.animation';
import { UserStoreService } from '../../../services/state-management/user-store.service';
import { KzIconsRegistryService } from '../../../../kz-ui/ui/icon/kz-icons-registry.service';
import { kzIconsCross } from '../../../../kz-ui/ui/icon/generated-icons/kzIcons-cross.icon';
import { HttpParams } from '@angular/common/http';
import { MediaService } from '@adeo/ngx-kozikaza-api/media';

export enum GaleryContainerImageSize {
  Small = 'kz-kaza-gallery-photos--small' as any,
  Medium = 'kz-kaza-gallery-photos--medium' as any,
  Big = 'kz-kaza-gallery-photos--big' as any
}

export enum GaleryImageSize {
  Small = 'small' as any,
  Medium = 'medium' as any,
  Big  = 'big' as any
}

@Component({
  selector: 'app-img-gallery',
  templateUrl: './img-gallery.component.html',
  styleUrls: ['./img-gallery.component.scss'],
  animations: [fadeInAnimation, modalEntranceAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImgGalleryComponent implements OnChanges, OnInit, OnDestroy {

  @Input() kazaId: string;
  @Input() imgContainerSize: GaleryContainerImageSize = GaleryContainerImageSize.Small;
  @Input() imgSize: GaleryImageSize = GaleryImageSize.Medium;
  @Input() onlyPostIdeas = 0;
  @Input() preloadThumbs = false;
  @Input() preloadSize: GaleryImageSize = GaleryImageSize.Small;
  @Output() removedMedia: EventEmitter<string> = new EventEmitter<string>();

  public imgSizes = GaleryImageSize;

  protected mediasSubscription: Subscription;

  kaza: any;

  medias: MediaDisplay[] = [];

  public imgLoading: boolean[] = [];
  public mediasLoading = false;
  public requestDoneOnce = false;

  public _diapoActive = -1;
  public totalSlides = 0;

  protected userLogged: User;
  protected userLoggedIsAdmin: boolean;

  public kazaOfLoggedUser = false;
  public isFavoriteGallery = false;

  protected ratioOffset = 0.3;
  protected ratioOffsetB = 0.5;

  protected params = {
    page: 1,
    kaza: null,
    order: null,
    postIdea: null,
    attributeValues: null,
    landing: null,
    withAttributeValues: null,
    privacyForUser: null
  };

  constructor(
    protected mediaService: MediaService,
    protected cdr: ChangeDetectorRef,
    protected toastrService: ToastrService,
    protected route: ActivatedRoute,
    protected seoService: SeoService,
    @Inject(PLATFORM_ID) protected platformId,
    protected readonly dataLayerService: DataLayerService,
    protected kzIconsRegistryService: KzIconsRegistryService,
    public readonly userStoreService: UserStoreService,
  ) {
    this.kzIconsRegistryService.registerIcons([kzIconsCross]);
  }

  ngOnInit() {
  }

  // Opening component from Template (use Component @Input)
  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      if (propName === 'kazaId' || propName === 'onlyPostIdeas') {
        this.initKazaParams();

        // Privacy management
        // this.userSubscription = observableForkJoin([
        //   this.userService.isAdmin().pipe(take(1)),
        //   this.userService.loggedUser.pipe(take(1))]
        // ).pipe(map((data: any[]) => {
        //   this.userLoggedIsAdmin = data[0];
        //   this.userLogged = data[1];
        //   if (!this.userLoggedIsAdmin) {
        //     this.params['privacyForUser'] = [];
        //     this.params['privacyForUser'][0] = Privacy.Public;
        //   }
        // }))
        //   .subscribe(
        //     () => this.updateMediasList(1),
        //     (err) => this.toastrService.error('Une erreur est survenue.
        //     Impossible de récupérer l\'utilsateur courant. Veuillez recommencer')
        //   );
      }
    }
  }

  ngOnDestroy() {
    if (this.mediasSubscription && !this.mediasSubscription.closed) {
      this.mediasSubscription.unsubscribe();
    }
  }

  initKazaParams() {
    this.params.kaza = this.kazaId;
    this.params.order = { createdAt : RequestOrder.DESC };
    if (this.onlyPostIdeas != null) {
      this.params.postIdea = this.onlyPostIdeas;
    }
    this.updateMediasList(1);
  }

  set page(n: number) {
    this.params.page = n;
  }

  get page(): number {
    return this.params.page;
  }

  set requestOrder(requestOrder: RequestOrder) {
    this.params.order = { createdAt : requestOrder};
    this.medias = [];
    this.totalSlides = 0;
    this.updateMediasList(1);
  }

  get requestOrder(): RequestOrder {
    return (this.params.order) ? this.params.order.createdAt : RequestOrder.DESC;
  }

  onScroll() {
    if (this.medias.length && !this.mediasLoading) {
      this.updateMediasList();
    }
  }

  protected sendAnalytics(offset): void {
    this.dataLayerService.sendPageView({offset});
  }

  protected getMedias(): Observable<HydraCollection<MediaDisplay>> {
    return this.getMediasRequest();
  }

  protected getMediasRequest(): Observable<HydraCollection<MediaDisplay>> {
    const argsTemp: any = Object.assign({ itemsPerPage : MISC_CONST.NB_MEDIAS_OFFSET}, this.params);
    let order;
    let type;
    if (argsTemp.type && Array.isArray(argsTemp.type)) {
      type = argsTemp.type;
      delete argsTemp.type;
    }
    if (argsTemp.order) {
      order = argsTemp.order;
      delete argsTemp.order;
    }
    let params = new HttpParams({ fromObject: argsTemp });
    // if (privacy) { privacy.map((p) => params = params.append('privacy[]', p)); }
    if (type) { type.map((p) => params = params.append('type[]', p)); }
    params = (order && order.createdAt) ? params.append('order[createdAt]', order.createdAt) : params;

    const webServiceOptions: WebServiceOptions = {
      errorHandlerStrategy: ErrorHandlerStrategy.Ignore,
      refresh: true,
      refreshOnlyFirstLevel: true,
      query: params
    };
    return this.mediaService.getMedias(null, webServiceOptions).pipe(
      map((data) => data as HydraCollection<MediaDisplay>)
    );
  }

  public updateMediasList(page: number = null): void {
    this.mediasLoading = true;
    this.cdr.markForCheck();
    this.page = (page) ? page : (this.page + 1);
    this.medias = (page === 1) ? [] : this.medias;
    this.mediasSubscription = this.getMedias().pipe(
      catchError((err) => {
        this.toastrService.error($localize`:@@toast.could_not_load_medias:Could not load media`);
        return throwError(err);
      }),
      finalize(() =>  { this.mediasLoading = false; this.cdr.markForCheck(); }),
      tap((medias) => {
        if (this.page > 1) {
          this.sendAnalytics(MISC_CONST.NB_MEDIAS_OFFSET * (this.params.page - 1));
        }
        this.requestDoneOnce = true;
        this.medias = (this.page > 1) ?
          [...this.medias, ...medias['hydra:member'].map((m) => { m.ratio = this.setRatioClass(m.width, m.height); return m; })] :
          medias['hydra:member'].map((m) => { m.ratio = this.setRatioClass(m.width, m.height); return m; });
        this.totalSlides = medias['hydra:totalItems'];
        this.imgLoading = [...this.imgLoading, ...Array(this.totalSlides - this.imgLoading.length).fill(true)];
        this.cdr.markForCheck();
      })
    ).subscribe();
  }

  setRatioClass(width: number, height: number/*, index: number*/): MediaRatio {
    const ratio = width / height;
    return (ratio > (1 + this.ratioOffset)) ? 'ratioLandscape' : ((ratio < (1 - this.ratioOffset)) ? 'ratioPortrait' : 'ratioSquare');
  }

  public get diapoActive() {
    return this._diapoActive;
  }

  public set diapoActive(diapo: number) {
    this._diapoActive = diapo;
  }

  public showDiapo(mediaIndex: number) {
    this.diapoActive = mediaIndex;
    this.cdr.markForCheck();
  }

  imageLoaded(index: number) {
    setTimeout(function() {
      this.imgLoading[index] = false;
    }.bind(this), 200);
  }

  removeMedia(mediaId: string) {
    this.medias = this.medias.filter((media) => media['@id'] !== mediaId);
    this.removedMedia.emit(mediaId);
    this.cdr.detectChanges();
  }

  public trackMedia(index: number, item: Media): string {
    return (!item) ? null : item['@id'];
  }
}

export interface MediaDisplay extends Media {
  ratio: MediaRatio;
}

export type MediaRatio = 'ratioLandscape' | 'ratioPortrait' | 'ratioSquare';
